import React, { useState, useEffect, useRef } from 'react'
import {
  Bell,
  Info,
  AlertTriangle,
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { I } from '../shared/ns-icons'

const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: notificationsData, isLoading } = useQuery(
    ['notifications', 'dropdown'],
    () => apiService.get('/notifications?limit=5').then(res => res.data),
    {
      refetchInterval: isOpen ? 60000 : 600000,
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (error) => console.warn('Failed to fetch notifications:', error.message)
    }
  )

  const { data: unreadData } = useQuery(
    'notifications-unread-count',
    () => apiService.get('/notifications/unread-count').then(res => res.data),
    {
      refetchInterval: 600000,
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
      onError: (error) => console.warn('Failed to fetch unread count:', error.message)
    }
  )

  const markAsReadMutation = useMutation(
    (notificationId) => apiService.patch(`/notifications/${notificationId}/read`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      }
    }
  )

  const markAllAsReadMutation = useMutation(
    () => apiService.patch('/notifications/mark-all-read'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notifications')
        queryClient.invalidateQueries('notifications-unread-count')
      }
    }
  )

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getNotificationLink = (notification) => {
    const data = notification.data
      ? (typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data)
      : {}
    const role = user?.role
    const title = notification.title.toLowerCase()
    const message = notification.message.toLowerCase()
    const actionUrl = data.actionUrl || ''

    const extractId = (url) => {
      const match = url.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
      return match ? match[1] : null
    }

    if (
      title.includes('edit request') || message.includes('edit request') ||
      notification.type === 'edit_request' ||
      actionUrl.includes('/requests')
    ) {
      const id = data.requestId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/requests?open=${id}` : '/admin/requests'
      return id ? `/client/requests?open=${id}` : '/client/requests'
    }

    if (
      title.includes('support') || message.includes('support') ||
      notification.type === 'support' || notification.type === 'info' ||
      actionUrl.includes('/support')
    ) {
      const id = data.ticketId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/support?open=${id}` : '/admin/support'
      return id ? `/client/support?open=${id}` : '/client/support'
    }

    if (actionUrl && !actionUrl.includes('localhost:3000') && !actionUrl.includes('dashboard')) {
      return actionUrl
    }

    if (title.includes('site published') || message.includes('published') || notification.type === 'site_published') {
      if (role === 'admin') return data.siteId ? `/admin/sites/${data.siteId}` : '/admin/sites'
      return data.siteId ? `/client/sites/${data.siteId}` : '/client/sites'
    }

    if (
      title.includes('payment') || message.includes('payment') ||
      title.includes('billing') || notification.type === 'billing'
    ) {
      if (role === 'admin') return data.clientId ? `/admin/billing?client=${data.clientId}` : '/admin/billing'
      return '/client/billing'
    }

    if (title.includes('backup') || message.includes('backup') || notification.type === 'backup_completed') {
      if (role === 'admin') return data.backupId ? `/admin/backups?highlight=${data.backupId}` : '/admin/backups'
    }

    if (notification.type === 'user_activity') {
      if (role === 'admin') return data.userId ? `/admin/users/${data.userId}` : '/admin/users'
    }

    return role === 'admin' ? '/admin' : '/client'
  }

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) markAsReadMutation.mutate(notification.id)
    const link = getNotificationLink(notification)
    setIsOpen(false)
    if (link.startsWith('http')) {
      window.open(link, '_blank')
    } else {
      navigate(link)
    }
  }

  const getNotificationIcon = (type) => {
    const iconStyle = { width: '1rem', height: '1rem' }
    switch (type) {
      case 'success': return <CheckCircle style={{ ...iconStyle, color: 'var(--success)' }} />
      case 'warning': return <AlertTriangle style={{ ...iconStyle, color: 'var(--warning)' }} />
      case 'error':   return <AlertCircle style={{ ...iconStyle, color: 'var(--danger)' }} />
      default:        return <Info style={{ ...iconStyle, color: 'var(--accent)' }} />
    }
  }

  const getIconToneName = (type) => {
    switch (type) {
      case 'success': return 'success'
      case 'warning': return 'warning'
      case 'error':   return 'danger'
      default:        return ''
    }
  }

  const getTimeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime()
    const mins = Math.floor(diff / 60000)
    const hrs = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins}m`
    if (hrs < 24) return `${hrs}h`
    if (days < 7) return `${days}d`
    return new Date(timestamp).toLocaleDateString()
  }

  const notifications = notificationsData?.notifications || []
  const unreadCount = unreadData?.count || 0

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell button — matches the prototype: ns bell + small accent dot */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-icon btn-ghost"
        style={{ position: 'relative', background: isOpen ? 'var(--surface-hover)' : undefined }}
        title="Notifications"
      >
        <I.bell />
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
        )}
      </button>

      {/* Dropdown — 1:1 with prototype NSNotifPopover */}
      {isOpen && (
        <div className="card" style={{
          position: 'absolute',
          right: 0,
          top: 'calc(100% + 0.75rem)',
          width: 380,
          maxWidth: '92vw',
          zIndex: 60,
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>Notifications</h3>
            {unreadCount > 0 && <span className="badge badge-accent" style={{ fontSize: 10 }}>{unreadCount} new</span>}
            <button
              className="btn btn-sm btn-ghost"
              style={{ marginLeft: 'auto' }}
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isLoading || unreadCount === 0}
            >
              Mark all read
            </button>
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {isLoading ? (
              <div style={{ padding: '2.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }} className="text-muted">
                <div style={{
                  width: '1rem',
                  height: '1rem',
                  border: '2px solid var(--accent)',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.7s linear infinite',
                }} />
                <span style={{ fontSize: '0.875rem' }}>Loading…</span>
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center' }}>
                <div style={{
                  width: '3rem',
                  height: '3rem',
                  margin: '0 auto 0.75rem',
                  background: 'var(--bg-1)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Bell style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-faint)' }} />
                </div>
                <p className="font-medium" style={{ fontSize: '0.875rem' }}>No notifications</p>
                <p className="text-faint" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  We'll notify you when something happens.
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    gap: 11,
                    padding: '13px 16px',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    borderLeft: notification.is_read ? '2px solid transparent' : '2px solid var(--accent)',
                    background: notification.is_read ? 'transparent' : 'var(--surface)',
                    cursor: 'pointer',
                    transition: 'background 0.12s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = notification.is_read ? 'transparent' : 'var(--surface)'}
                >
                  <span className={`icon-tile ${getIconToneName(notification.type)}`} style={{ width: 32, height: 32, fontSize: 14, flexShrink: 0 }}>
                    {getNotificationIcon(notification.type)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{notification.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>{notification.message}</div>
                  </div>
                  <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                    {getTimeAgo(notification.created_at)}
                  </span>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <button
            className="btn btn-ghost"
            style={{ width: '100%', borderRadius: 0, padding: 12, color: 'var(--accent-3)' }}
            onClick={() => {
              setIsOpen(false)
              navigate(user?.role === 'admin' ? '/admin/notifications' : '/client/notifications')
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  )
}

export default NotificationDropdown
